import React, { useState, useEffect } from 'react';
import { X, ExternalLink, FileText, Shield, BookOpen, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { FrameworkBadge } from './FrameworkBadge';
import { FrameworkStandard } from './FrameworkList';

interface FrameworkDetailModalProps {
  framework: FrameworkStandard | null;
  isOpen: boolean;
  onClose: () => void;
  onLoadControls?: (frameworkId: number) => Promise<any[]>;
  onLoadCitations?: (frameworkId: number) => Promise<any[]>;
}

export const FrameworkDetailModal: React.FC<FrameworkDetailModalProps> = ({
  framework,
  isOpen,
  onClose,
  onLoadControls,
  onLoadCitations
}) => {
  const [controls, setControls] = useState<any[]>([]);
  const [loadingControls, setLoadingControls] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [citations, setCitations] = useState<any[]>([]);
  const [loadingCitations, setLoadingCitations] = useState(false);
  const [showCitations, setShowCitations] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'requirements' | 'controls' | 'citations'>('overview');

  useEffect(() => {
    if (isOpen && framework && showControls && onLoadControls) {
      loadControls();
    }
  }, [isOpen, framework, showControls]);

  useEffect(() => {
    if (isOpen && framework && showCitations && onLoadCitations) {
      loadCitations();
    }
  }, [isOpen, framework, showCitations]);

  const loadControls = async () => {
    if (!framework || !onLoadControls) return;

    setLoadingControls(true);
    try {
      const controlsData = await onLoadControls(framework.id);
      setControls(controlsData);
    } catch (error) {
      console.error('Error loading controls:', error);
    } finally {
      setLoadingControls(false);
    }
  };

  const loadCitations = async () => {
    if (!framework || !onLoadCitations) return;

    setLoadingCitations(true);
    try {
      const citationsData = await onLoadCitations(framework.id);
      setCitations(citationsData);
    } catch (error) {
      console.error('Error loading citations:', error);
    } finally {
      setLoadingCitations(false);
    }
  };

  if (!isOpen || !framework) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="h-8 w-8" />
                <h2 className="text-2xl font-bold">{framework.framework_name}</h2>
              </div>
              <p className="text-blue-100 text-sm mb-3">{framework.standard_id}</p>
              <h3 className="text-xl font-medium">{framework.standard_name}</h3>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-4 border-t border-white/20 pt-4">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'overview'
                  ? 'bg-white text-blue-600'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('requirements')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'requirements'
                  ? 'bg-white text-blue-600'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Requirements
            </button>
            {onLoadControls && (
              <button
                onClick={() => {
                  setActiveTab('controls');
                  setShowControls(true);
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'controls'
                    ? 'bg-white text-blue-600'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                Related Controls
              </button>
            )}
            {onLoadCitations && (
              <button
                onClick={() => {
                  setActiveTab('citations');
                  setShowCitations(true);
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'citations'
                    ? 'bg-white text-blue-600'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                Citations
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">Basic Information</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-gray-600">Framework:</span>
                      <div className="mt-1">
                        <FrameworkBadge frameworkName={framework.framework_name} size="md" />
                      </div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Standard ID:</span>
                      <p className="text-sm text-gray-800 mt-1 font-mono">{framework.standard_id}</p>
                    </div>
                  </div>

                  {framework.standard_number && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Standard Number:</span>
                      <p className="text-sm text-gray-800 mt-1">{framework.standard_number}</p>
                    </div>
                  )}

                  {framework.category && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Category:</span>
                      <p className="text-sm text-gray-800 mt-1">{framework.category}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {framework.description && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3 flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    Description
                  </h4>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-gray-800 leading-relaxed">{framework.description}</p>
                  </div>
                </div>
              )}

              {/* References */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  References
                </h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  {framework.statutory_reference && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-600 min-w-[120px]">Statutory Ref:</span>
                      <span className="text-sm text-gray-800">{framework.statutory_reference}</span>
                    </div>
                  )}
                  {framework.section_reference && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-600 min-w-[120px]">Section:</span>
                      <span className="text-sm text-gray-800">{framework.section_reference}</span>
                    </div>
                  )}
                  {framework.page_number && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-600 min-w-[120px]">Page Number:</span>
                      <span className="text-sm text-gray-800">{framework.page_number}</span>
                    </div>
                  )}
                  {framework.document_url && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-600 min-w-[120px]">Document:</span>
                      <a
                        href={framework.document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        View Official Document
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Requirements Tab */}
          {activeTab === 'requirements' && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-500 uppercase flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Compliance Requirements
              </h4>
              {framework.requirements ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">
                    {framework.requirements}
                  </p>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600">No specific requirements documented</p>
                </div>
              )}
            </div>
          )}

          {/* Controls Tab */}
          {activeTab === 'controls' && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-500 uppercase flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Related Control Standards
              </h4>

              {loadingControls ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading controls...</span>
                </div>
              ) : controls.length > 0 ? (
                <div className="space-y-3">
                  {controls.map((control) => (
                    <div
                      key={control.id}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h5 className="font-medium text-gray-800">{control.control_name}</h5>
                          <p className="text-xs text-gray-500 mt-1 font-mono">{control.control_id}</p>
                        </div>
                        {control.control_family && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                            {control.control_family}
                          </span>
                        )}
                      </div>
                      {control.description && (
                        <p className="text-sm text-gray-600 mt-2">{control.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <Shield className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600">No controls found for this framework standard</p>
                </div>
              )}
            </div>
          )}

          {/* Citations Tab */}
          {activeTab === 'citations' && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-500 uppercase flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Compliance Citations
              </h4>

              {loadingCitations ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading citations...</span>
                </div>
              ) : citations.length > 0 ? (
                <div className="space-y-3">
                  {citations.map((citation) => {
                    const severityColors: Record<string, { bg: string; border: string; text: string; badge: string }> = {
                      critical: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-900', badge: 'bg-red-600 text-white' },
                      high: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-900', badge: 'bg-orange-500 text-white' },
                      medium: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-900', badge: 'bg-yellow-500 text-white' },
                      low: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900', badge: 'bg-blue-500 text-white' },
                      informational: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-900', badge: 'bg-gray-500 text-white' }
                    };

                    const colors = severityColors[citation.severity] || severityColors.informational;

                    return (
                      <div
                        key={citation.id}
                        className={`border rounded-lg p-4 ${colors.bg} ${colors.border}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors.badge}`}>
                                {citation.severity?.toUpperCase()}
                              </span>
                              <span className="px-2 py-0.5 rounded text-xs bg-gray-200 text-gray-700">
                                {citation.citation_type}
                              </span>
                            </div>
                            <p className={`text-sm font-medium ${colors.text} mt-2`}>
                              {citation.citation_text}
                            </p>
                          </div>
                        </div>

                        {citation.violation_description && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-sm text-gray-700">
                              <span className="font-medium">Violation: </span>
                              {citation.violation_description}
                            </p>
                          </div>
                        )}

                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                          {citation.standard_reference && (
                            <div>
                              <span className="text-gray-600">Standard: </span>
                              <span className="text-gray-800 font-mono">{citation.standard_reference}</span>
                            </div>
                          )}
                          {citation.page_number && (
                            <div>
                              <span className="text-gray-600">Page: </span>
                              <span className="text-gray-800">{citation.page_number}</span>
                            </div>
                          )}
                          {citation.section_reference && (
                            <div>
                              <span className="text-gray-600">Section: </span>
                              <span className="text-gray-800">{citation.section_reference}</span>
                            </div>
                          )}
                          {citation.statutory_reference && (
                            <div>
                              <span className="text-gray-600">Statutory Ref: </span>
                              <span className="text-gray-800 font-mono text-xs">{citation.statutory_reference}</span>
                            </div>
                          )}
                        </div>

                        {citation.remediation_required && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center gap-2">
                              <AlertCircle className="h-4 w-4 text-orange-600" />
                              <span className="text-sm font-medium text-orange-900">Remediation Required</span>
                            </div>
                            {citation.remediation_recommendation && (
                              <p className="text-sm text-gray-700 mt-1 ml-6">{citation.remediation_recommendation}</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600">No compliance citations found for this framework standard</p>
                  <p className="text-xs text-gray-500 mt-1">Citations are generated when violations are identified during risk assessments</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex justify-end gap-3">
            {framework.document_url && (
              <a
                href={framework.document_url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                View Official Document
              </a>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
